import { UseState } from '@elearning-platform/ui';
import { useCallback, useEffect, useState } from 'react';

import { localStorageCryptKey } from '../consts';
import { LocalStorageValue } from '../interfaces/internal';
import { UseLocalStorage } from '../types/internal';
import { cryptoUtils } from '../utils';

export const useLocalStorage: <T = string>(key: string, initialValue: T) => UseLocalStorage<T> = <T = string>(
  key: string, initialValue: T
): UseLocalStorage<T> => {
  const readValue: () => Promise<T> = useCallback(async (): Promise<T> => {
    try {
      const item: string = window.localStorage.getItem(key) || '';
      if (!item) {
        return initialValue;
      }

      return JSON.parse(await cryptoUtils(item, localStorageCryptKey, 'decrypt')) as T;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_: unknown) {
      return initialValue;
    }
  }, [initialValue, key]);

  const [storedValue, setStoredValue]: UseState<LocalStorageValue<T>>
    = useState<LocalStorageValue<T>>({ isLoading: true, value: initialValue });

  const setValue: (value: T) => void = useCallback((value: T): void => {
    try {
      if (value === null) {
        window.localStorage.removeItem(key);
      } else {
        cryptoUtils(
          JSON.stringify(value), localStorageCryptKey, 'encrypt'
        )
          .then((encryptedValue: string): void => window.localStorage.setItem(key, encryptedValue))
          .catch((e: Error): Error => e);
      }
      setStoredValue((prev: LocalStorageValue<T>): LocalStorageValue<T> => {
        if (!prev.isLoading && JSON.stringify(prev.value) === JSON.stringify(value)) {
          return prev;
        }

        return { isLoading: false, value };
      });
      window.dispatchEvent(new CustomEvent('local-storage', { detail: { key } }));
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_: unknown) {
      return;
    }
  }, [key]);

  useEffect(
    (): () => void => {
      void readValue().then((value: T): void => setStoredValue({ isLoading: false, value }));
      const handleStorageChange: (event: Event) => void = (event: Event): void => {
        if (event instanceof CustomEvent && event.detail?.key !== key) {
          return;
        }

        void readValue().then((value: T): void => setStoredValue((prev: LocalStorageValue<T>): LocalStorageValue<T> => {
          if (!prev.isLoading && JSON.stringify(prev.value) === JSON.stringify(value)) {
            return prev;
          }

          return { isLoading: false, value };
        }));
      };
      window.addEventListener('storage', handleStorageChange);
      window.addEventListener('local-storage', handleStorageChange);

      return (): void => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('local-storage', handleStorageChange);
      };
    },
    [readValue, key]
  );

  return [storedValue, setValue];
};
